import { useEffect, useRef } from "react";
import ScrollRevealText from "./ScrollRevealText.jsx";
import Eyebrow from "./Eyebrow.jsx";
import "./WhatWeDo2.css";

// Line icons on a 40x40 grid; each entry is one path.
const icon = (paths) => (
  <svg
    aria-hidden="true"
    viewBox="0 0 40 40"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    xmlns="http://www.w3.org/2000/svg"
  >
    {paths.map((d, i) => (
      <path key={i} d={d} />
    ))}
  </svg>
);

const SERVICES = [
  {
    title: "Product Development",
    items: [
      "SaaS Platforms",
      "Web Applications",
      "Mobile Applications",
      "MVP Development",
      "Custom Business Software",
      "Enterprise Platforms",
    ],
    icon: icon([
      "M7 6H33A3 3 0 0 1 36 9V31A3 3 0 0 1 33 34H7A3 3 0 0 1 4 31V9A3 3 0 0 1 7 6Z",
      "M4 14H36",
      "M16 19L11.5 24L16 29",
      "M24 19L28.5 24L24 29",
    ]),
  },
  {
    title: "AI & Automation",
    items: [
      "AI Workflow Automation",
      "Conversational AI",
      "AI Agents",
      "AI Assistants",
      "Document Intelligence",
      "AI-Powered Search",
      "Process Automation",
      "LLM Integration",
    ],
    icon: icon([
      "M16 8C17.2 17 19 18.8 28 20C19 21.2 17.2 23 16 32C14.8 23 13 21.2 4 20C13 18.8 14.8 17 16 8Z",
      "M30 4C30.6 8.4 31.6 9.4 36 10C31.6 10.6 30.6 11.6 30 16C29.4 11.6 28.4 10.6 24 10C28.4 9.4 29.4 8.4 30 4Z",
      "M31 27C31.4 30 32 30.6 35 31C32 31.4 31.4 32 31 35C30.6 32 30 31.4 27 31C30 30.6 30.6 30 31 27Z",
    ]),
  },
  {
    title: "Business Systems",
    items: [
      "CRM & Sales Automation",
      "ERP",
      "POS",
      "Inventory Management",
      "HRM",
      "Customer Support Systems",
      "Business Dashboards",
      "Internal Tools",
    ],
    icon: icon([
      "M7 5H33A3 3 0 0 1 36 8V32A3 3 0 0 1 33 35H7A3 3 0 0 1 4 32V8A3 3 0 0 1 7 5Z",
      "M12 28V22",
      "M20 28V14",
      "M28 28V19",
      "M10 11H16",
    ]),
  },
  {
    title: "Digital Commerce",
    items: [
      "E-commerce",
      "Marketplace Platforms",
      "Order Management",
      "Payment Integration",
      "Social Commerce",
      "Omnichannel Commerce",
    ],
    icon: icon([
      "M3 6H8L12 24H31L35 11H9.5",
      "M12.5 31.5A2.5 2.5 0 1 0 17.5 31.5A2.5 2.5 0 1 0 12.5 31.5",
      "M25.5 31.5A2.5 2.5 0 1 0 30.5 31.5A2.5 2.5 0 1 0 25.5 31.5",
    ]),
  },
  {
    title: "Experience & Design",
    items: [
      "UI/UX Design",
      "Product Design",
      "Design Systems",
      "Web Design",
      "Prototyping",
      "Conversion-focused Websites",
    ],
    icon: icon([
      "M20 36L11 17L20 4L29 17Z",
      "M17.5 18.5A2.5 2.5 0 1 0 22.5 18.5A2.5 2.5 0 1 0 17.5 18.5",
      "M20 21V36",
      "M13 36H27",
    ]),
  },
  {
    title: "Infrastructure & Integration",
    items: [
      "API Development",
      "Third-party Integrations",
      "Cloud Infrastructure",
      "Database Architecture",
      "System Integration",
      "Deployment & Maintenance",
    ],
    icon: icon([
      "M5 9C5 6.2 11.7 4 20 4S35 6.2 35 9S28.3 14 20 14S5 11.8 5 9Z",
      "M5 9V20C5 22.8 11.7 25 20 25S35 22.8 35 20V9",
      "M5 20V31C5 33.8 11.7 36 20 36S35 33.8 35 31V20",
    ]),
  },
];

const DESKTOP = "(min-width: 992px)";
// phones in portrait: the header pins and the cards pile into the space beneath it
const PHONE = "(max-width: 767px) and (min-height: 501px)";
// where each buried card rests in the phone pile: sideways px, upward px, tilt deg (the top card is always straight)
const PILE = [
  [-8, -6, -3.5],
  [9, -4, 3],
  [-6, -8, -2.2],
  [7, -5, 2.6],
  [-9, -7, -4],
  [0, 0, 0],
];
const HOLD = 0.06; // fraction of the pin held still at each end
const clamp01 = (v) => Math.min(1, Math.max(0, v));
// Reads a length custom property in px (getComputedStyle returns "2rem" as-is).
const readPx = (el, name, fallback) => {
  const raw = getComputedStyle(el).getPropertyValue(name).trim();
  const n = parseFloat(raw);
  if (Number.isNaN(n)) return fallback;
  return raw.endsWith("rem")
    ? n * parseFloat(getComputedStyle(document.documentElement).fontSize)
    : n;
};
const easeOut = (q) => 1 - Math.pow(1 - q, 3);

/**
 * "What We Do" (stacked variant): same layout as WhatWeDo, but the cards pile
 * up on top of each other instead of scrolling past, like the testimonial deck
 * on the WowUX reference.
 *
 * - The section is tall and the grid inside is sticky. The first card sits in
 *   the deck; while the grid is pinned, each following card slides up from the
 *   bottom of the viewport and lands slightly lower than the one beneath it,
 *   so the edges of earlier cards stay visible as a stack.
 * - Scroll progress is smoothed with an exponential ease (like GSAP scrub).
 * - On portrait phones the heading block pins and the cards land in the space
 *   below it as a loose pile: each buried card tilts and shifts a little
 *   differently, and the top one is straight.
 * - Tablets and landscape phones drop the pin/scrub and the stack is pure CSS:
 *   every card is `position: sticky` with a growing top offset.
 */
export default function WhatWeDo2({
  heading = "Everything you need to build, automate, and scale",
  text = "From first product to full business systems, we design, build, and integrate the software and AI your company runs on.",
  cta = { label: "Start a project", href: "#CTA-Form" },
  steps = SERVICES,
}) {
  const containerRef = useRef(null);
  const gridRef = useRef(null);
  const cardsRef = useRef(null);

  useEffect(() => {
    const column = cardsRef.current;
    const container = containerRef.current;
    const grid = gridRef.current;
    if (!column || !container || !grid) return;

    const cards = Array.from(column.children);
    const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const desktop = window.matchMedia(DESKTOP);
    const phone = window.matchMedia(PHONE);
    const pinned = () => desktop.matches || phone.matches;

    let frame = null;
    let last = 0;
    let current = 0; // smoothed scrub progress actually applied (0..1)
    let target = 0;
    let near = true; // only scrolls while the section is on (or near) screen need work

    // Computed-style reads force a style recalc, so layout-ish numbers are read on resize, not per frame.
    let peek = 32;
    let peekX = 32;
    let pinTop = 0;
    let range = 0;
    let wrapTop = 0; // cards wrapper's offset inside the pinned grid
    const readMetrics = () => {
      wrapTop = column.parentElement.offsetTop;
      peek = readPx(column, "--hw2-peek", 32);
      peekX = readPx(column, "--hw2-peek-x", 32);
      pinTop = parseFloat(getComputedStyle(grid).top) || 0;
      range = container.offsetHeight - grid.offsetHeight;
    };

    const measure = () => {
      if (!pinned()) {
        target = 0;
        return;
      }
      // Driven only while the grid is pinned; first/last HOLD is dead space.
      const p = range > 0 ? (pinTop - container.getBoundingClientRect().top) / range : 0;
      target = clamp01((p - HOLD) / (1 - 2 * HOLD));
    };

    const apply = () => {
      if (!pinned()) {
        cards.forEach((card) => {
          card.style.transform = "";
          card.style.visibility = "";
          card.classList.remove("is-active");
        });
        return;
      }

      const steps = Math.max(1, cards.length - 1);
      let top = 0;

      if (phone.matches) {
        const slide = window.innerHeight - pinTop - wrapTop; // card top -> viewport bottom
        cards.forEach((card, i) => {
          const [dx, dy, tilt] = PILE[i % PILE.length];
          const q = i === 0 ? 1 : clamp01(current * steps - (i - 1)); // arriving
          const b = clamp01(current * steps - i); // being buried by the next card
          const land = easeOut(q);
          const bury = easeOut(b);
          const y = (1 - land) * slide + dy * bury;
          // arrives tilted the opposite way, straightens as it lands, then settles into the pile as the next one covers it
          const rot = -tilt * 0.8 * (1 - land) + tilt * bury;
          card.style.transform = `translate3d(${dx * bury}px, ${y}px, 0) rotate(${rot}deg)`;
          card.style.visibility = q > 0 ? "" : "hidden";
          if (q >= 0.5) top = i;
        });
        cards.forEach((card, i) => card.classList.toggle("is-active", i === top));
        return;
      }

      const fromBottom = window.innerHeight - pinTop; // wrapper top -> viewport bottom

      cards.forEach((card, i) => {
        // card 0 is already in the deck; card i enters during segment i
        const q = i === 0 ? 1 : clamp01(current * steps - (i - 1));
        const y = i * peek + (1 - easeOut(q)) * (fromBottom - i * peek);
        // zigzag: 0, 1, 2, 1, 0, 1, 2 ... steps to the right, like the reference deck
        const m = i % 4;
        const x = (m <= 2 ? m : 4 - m) * peekX;
        card.style.transform = `translate3d(${x}px, ${y}px, 0)`;
        card.style.visibility = q > 0 ? "" : "hidden";
        if (q >= 0.5) top = i;
      });

      cards.forEach((card, i) => card.classList.toggle("is-active", i === top));
    };

    const tick = (now) => {
      frame = null;
      const dt = last ? Math.min(0.1, (now - last) / 1000) : 1 / 60;
      last = now;

      measure();

      const k = reduceMotion ? 1 : 1 - Math.exp(-dt * 8);
      current += (target - current) * k;
      if (Math.abs(target - current) < 0.0005) current = target;
      apply();

      if (current !== target) frame = requestAnimationFrame(tick);
      else last = 0;
    };

    const request = () => {
      if (frame == null) frame = requestAnimationFrame(tick);
    };
    const onScroll = () => near && request();
    const onLayout = () => {
      readMetrics();
      request();
    };

    readMetrics();
    request();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onLayout);
    desktop.addEventListener?.("change", onLayout);
    phone.addEventListener?.("change", onLayout);
    // the section's height settles as fonts and neighbours load
    const ro = new ResizeObserver(onLayout);
    ro.observe(container);
    ro.observe(grid);
    // off screen, a scroll can't change anything; on the way out, one last pass settles the cards
    const io = new IntersectionObserver(
      ([entry]) => {
        near = entry.isIntersecting;
        request();
      },
      { rootMargin: "200px 0px" }
    );
    io.observe(container);

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onLayout);
      desktop.removeEventListener?.("change", onLayout);
      phone.removeEventListener?.("change", onLayout);
      ro.disconnect();
      io.disconnect();
      if (frame) cancelAnimationFrame(frame);
    };
  }, [steps]);

  return (
    <section
      className="hw2"
      id="services-stack"
      style={{ "--hw2-count": steps.length }}
    >
      <div className="hw2-inner">
        <div className="hw2-container" ref={containerRef}>
          <div className="hw2-grid" ref={gridRef}>
            <div className="hw2-content">
              <div className="hw2-info">
                <Eyebrow section="services" />
                <ScrollRevealText
                  as="h2"
                  className="hw2-heading"
                  text={heading}
                  unit="word"
                  minOpacity={0.1}
                  startAt={0.8}
                  endAt={0.3}
                  endAnchor={0.5}
                />
                <p className="hw2-text">{text}</p>
                {cta && (
                  <a className="hw2-btn" href={cta.href}>
                    <span className="hw2-btn-bg" aria-hidden="true" />
                    <span className="hw2-btn-text">{cta.label}</span>
                  </a>
                )}
              </div>
            </div>

            <div className="hw2-cards-wrapper">
              <ol className="hw2-cards" ref={cardsRef}>
                {steps.map((step, i) => (
                  <li className="hw2-card" key={step.title} style={{ "--i": i }}>
                    <div className="hw2-card-top">
                      <div className="hw2-card-icon">{step.icon}</div>
                      <span className="hw2-card-number">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                    </div>
                    <div className="hw2-card-content">
                      <h3 className="hw2-card-title">{step.title}</h3>
                      <ul className="hw2-card-list">
                        {step.items.map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
